import {
    DollarSign,
    Receipt,
    ShoppingBag,
    Users,
    CreditCard,
    Percent,
    Clock,
    TrendingUp,
    LineChart,
    BarChart3,
    PieChart,
    Sun,
    Grid3x3,
    UserPlus,
    UserCheck,
    Repeat,
    UserX,
    Wallet,
    Trophy,
    BarChart4,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Widget catalog
//
// Every KPI card and chart available on the Activity dashboard is declared here.
// `dataSource` ("summary" | "performance") tells the container which API query to
// enable — queries only run when at least one *visible* widget needs them.
//
// All widgets are HIDDEN by default (see useKpiPreferences); the user opts in via
// the selector panel ("Show More KPIs").
// ─────────────────────────────────────────────────────────────────────────────

const get = (obj, path) =>
    path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

export const KPI_DEFINITIONS = [
    // ── Summary KPIs ────────────────────────────────────────────────────────
    {
        id: "revenue",
        labelKey: "kpi.revenue",
        dataSource: "summary",
        accessor: (d) => get(d, "kpis.revenue"),
        format: "currency",
        icon: DollarSign,
        accent: "#4DB6AC",
    },
    {
        id: "revenue_ht",
        labelKey: "kpi.revenue_ht",
        dataSource: "summary",
        accessor: (d) => get(d, "kpis.revenue_ht"),
        format: "currency",
        icon: Receipt,
        accent: "#5C6BC0",
    },
    {
        id: "orders_count",
        labelKey: "kpi.orders_count",
        dataSource: "summary",
        accessor: (d) => get(d, "kpis.orders_count"),
        format: "number",
        icon: ShoppingBag,
        accent: "#26A69A",
    },
    {
        id: "clients_count",
        labelKey: "kpi.clients_count",
        dataSource: "summary",
        accessor: (d) => get(d, "kpis.clients_count"),
        format: "number",
        icon: Users,
        accent: "#7E57C2",
    },
    {
        id: "average_order_value",
        labelKey: "kpi.average_order_value",
        dataSource: "summary",
        accessor: (d) => get(d, "kpis.average_order_value"),
        format: "currency",
        icon: CreditCard,
        accent: "#FFA726",
    },
    {
        id: "total_discounts",
        labelKey: "kpi.total_discounts",
        dataSource: "summary",
        accessor: (d) => get(d, "kpis.total_discounts"),
        format: "currency",
        // More discounts given = lower margin, so a rise is shown in red.
        invertTrend: true,
        icon: Percent,
        accent: "#EF5350",
    },
    // ── Performance KPIs ────────────────────────────────────────────────────
    {
        id: "peak_hour",
        labelKey: "kpi.peak_hour",
        dataSource: "performance",
        accessor: (d) => get(d, "peak_hour.label"),
        format: "text",
        icon: Clock,
        accent: "#42A5F5",
    },
    {
        id: "peak_hour_revenue",
        labelKey: "kpi.peak_hour_revenue",
        dataSource: "performance",
        accessor: (d) => get(d, "peak_hour.revenue"),
        format: "currency",
        icon: TrendingUp,
        accent: "#66BB6A",
    },
    {
        id: "peak_hour_orders",
        labelKey: "kpi.peak_hour_orders",
        dataSource: "performance",
        accessor: (d) => get(d, "peak_hour.orders_count"),
        format: "number",
        icon: ShoppingBag,
        accent: "#26C6DA",
    },
    // ── Customer retention KPIs ─────────────────────────────────────────────
    {
        id: "active_clients",
        labelKey: "kpi.active_clients",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.active_clients"),
        format: "number",
        icon: Users,
        accent: "#26A69A",
    },
    {
        id: "new_clients",
        labelKey: "kpi.new_clients",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.new_clients"),
        format: "number",
        icon: UserPlus,
        accent: "#66BB6A",
    },
    {
        id: "returning_clients",
        labelKey: "kpi.returning_clients",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.returning_clients"),
        format: "number",
        icon: UserCheck,
        accent: "#5C6BC0",
    },
    {
        id: "repeat_rate",
        labelKey: "kpi.repeat_rate",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.repeat_rate"),
        format: "percent",
        icon: Repeat,
        accent: "#4DB6AC",
    },
    {
        id: "new_client_rate",
        labelKey: "kpi.new_client_rate",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.new_client_rate"),
        format: "percent",
        icon: UserPlus,
        accent: "#FFA726",
    },
    {
        id: "avg_orders_per_client",
        labelKey: "kpi.avg_orders_per_client",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.avg_orders_per_client"),
        format: "number",
        icon: ShoppingBag,
        accent: "#42A5F5",
    },
    {
        id: "avg_spend_per_client",
        labelKey: "kpi.avg_spend_per_client",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.avg_spend_per_client"),
        format: "currency",
        icon: Wallet,
        accent: "#AB47BC",
    },
    {
        id: "anonymous_orders",
        labelKey: "kpi.anonymous_orders",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.anonymous_orders"),
        format: "number",
        icon: UserX,
        accent: "#78909C",
    },
    {
        id: "anonymous_revenue",
        labelKey: "kpi.anonymous_revenue",
        dataSource: "retention",
        accessor: (d) => get(d, "kpis.anonymous_revenue"),
        format: "currency",
        icon: UserX,
        accent: "#EF5350",
    },
];

export const CHART_DEFINITIONS = [
    {
        id: "daily_revenue_trend",
        labelKey: "chart.daily_revenue_trend",
        dataSource: "summary",
        icon: TrendingUp,
        span: 2,
    },
    {
        id: "orders_trend",
        labelKey: "chart.orders_trend",
        dataSource: "summary",
        icon: LineChart,
        span: 2,
    },
    {
        id: "payment_methods",
        labelKey: "chart.payment_methods",
        dataSource: "summary",
        icon: PieChart,
        span: 1,
    },
    {
        id: "revenue_by_hour",
        labelKey: "chart.revenue_by_hour",
        dataSource: "performance",
        icon: BarChart3,
        span: 2,
    },
    {
        id: "revenue_by_period",
        labelKey: "chart.revenue_by_period",
        dataSource: "performance",
        icon: Sun,
        span: 1,
    },
    {
        id: "activity_heatmap",
        labelKey: "chart.activity_heatmap",
        dataSource: "performance",
        icon: Grid3x3,
        span: 2,
    },
    {
        id: "new_vs_returning",
        labelKey: "chart.new_vs_returning",
        dataSource: "retention",
        icon: Users,
        span: 1,
    },
    {
        id: "frequency_distribution",
        labelKey: "chart.frequency_distribution",
        dataSource: "retention",
        icon: BarChart4,
        span: 1,
    },
    {
        id: "top_clients",
        labelKey: "chart.top_clients",
        dataSource: "retention",
        icon: Trophy,
        span: 2,
    },
];

// Convenience lookups
export const KPI_BY_ID = Object.fromEntries(KPI_DEFINITIONS.map((k) => [k.id, k]));
export const CHART_BY_ID = Object.fromEntries(CHART_DEFINITIONS.map((c) => [c.id, c]));

export const ALL_KPI_IDS = KPI_DEFINITIONS.map((k) => k.id);
export const ALL_CHART_IDS = CHART_DEFINITIONS.map((c) => c.id);
